import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Hotel from "@mui/icons-material/Hotel";
import Spa from "@mui/icons-material/Spa";
import SportsGolf from "@mui/icons-material/SportsGolf";
import Storefront from "@mui/icons-material/Storefront";
import Groups from "@mui/icons-material/Groups";
import LocalActivity from "@mui/icons-material/LocalActivity";
import RestaurantMenu from "@mui/icons-material/RestaurantMenu";
import { Navigate } from "react-router-dom";
import { PreLoader } from "./subcomponents/Preloader";
import AlertComponent from "./subcomponents/AlertComponent";
import { getProductID, ProductData, ProductEnum } from "../constants/login";
import {
  clearSessionReadOnly,
  setSessionReadOnly,
} from "../constants/sessionReadOnly";
import {
  extractOnboardingToken,
  getOnboardingJwtForAuth,
  setOnboardingToken,
} from "../constants/onboardingToken";
import {
  getUserEmail,
  loginService,
  updateUserToken,
} from "./services/requests";
import { RoomPropertyEnum } from "../constants/NavbarList";
import rguestPmsBackground from "../assets/rguest-pms-background.jpg";
import "../components/styles/login.css";

const PRODUCT_ICONS: Partial<
  Record<ProductEnum, React.ComponentType<{ className?: string }>>
> = {
  [ProductEnum.VERSA]: Hotel,
  [ProductEnum.SPA]: Spa,
  [ProductEnum.GOLF]: SportsGolf,
  [ProductEnum.RETAIL]: Storefront,
  [ProductEnum.SALES_AND_CATETING]: Groups,
  [ProductEnum.RESERVE_ACTIVITIES]: LocalActivity,
  [ProductEnum.RESERVE_DINING]: RestaurantMenu,
};

const ProductIcon = ({ product }: { product: string }) => {
  const Icon = PRODUCT_ICONS[product as ProductEnum];
  if (!Icon) {
    return null;
  }
  return <Icon className="login-product-option__icon" />;
};

const isAdminRole = (role: unknown): boolean => {
  if (role == null) return false;
  if (typeof role === "string") {
    return role.trim().toLowerCase() === "admin";
  }
  return false;
};

const LoginPage = () => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [tenantID, setTenantID] = useState<string>("");
  const [propertyID, setPropertyID] = useState<string>("");
  const [productID, setProductID] = useState<string>(ProductEnum.VERSA);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);
  const [readOnlyDialogOpen, setReadOnlyDialogOpen] = useState(false);
  const [pendingRedirectPath, setPendingRedirectPath] = useState<string | null>(
    null,
  );

  const canSubmit = useMemo(
    () =>
      username.trim() !== "" &&
      password.trim() !== "" &&
      tenantID.trim() !== "" &&
      propertyID.trim() !== "" &&
      productID.trim() !== "",
    [username, password, tenantID, propertyID, productID],
  );

  const handleLogin = async () => {
    try {
      const tenantNum = Number(tenantID);
      const propertyNum = Number(propertyID);

      if (
        username.trim() !== "" &&
        password.trim() !== "" &&
        tenantID.trim() !== "" &&
        !Number.isNaN(tenantNum) &&
        propertyID.trim() !== "" &&
        !Number.isNaN(propertyNum) &&
        productID.trim() !== ""
      ) {
        clearSessionReadOnly();
        setLoading(true);
        const response: any = await loginService(
          username,
          password,
          tenantNum,
          propertyNum,
          productID,
        );
        if (response.status === 200) {
          let loginBody: {
            readOnly?: boolean;
            role?: string;
            active?: boolean;
            message?: string;
          } = {};
          try {
            loginBody = await response.json();
          } catch {
            loginBody = {};
          }

          if (loginBody.active === false) {
            clearSessionReadOnly();
            setAlertMessage(
              loginBody.message ??
                "Your account has been deactivated. Contact your administrator.",
            );
            setAlertType("error");
            setOpenAlert(true);
            return;
          }

          const productIdNum =
            getProductID(productID ?? ProductEnum.VERSA) ?? 0;

          let onboardingJwt =
            extractOnboardingToken(loginBody) ?? getOnboardingJwtForAuth();
          if (!onboardingJwt) {
            const tokenResponse = await updateUserToken(
              tenantNum,
              propertyNum,
              productIdNum,
            );
            if (tokenResponse instanceof Response && tokenResponse.ok) {
              try {
                onboardingJwt = extractOnboardingToken(
                  await tokenResponse.json(),
                );
              } catch {
                onboardingJwt = undefined;
              }
            }
          }
          if (onboardingJwt) {
            setOnboardingToken(onboardingJwt);
          }

          setSessionReadOnly(loginBody.readOnly === true);
          const baseQs = `tenantId=${tenantNum}&propertyId=${propertyNum}&productId=${productIdNum}`;
          const dashboardPath = `/dashboard?${baseQs}&component=${encodeURIComponent(
            RoomPropertyEnum.propertyInformation,
          )}`;
          let nextPath = dashboardPath;
          try {
            const profile: unknown = await getUserEmail();
            if (
              profile &&
              typeof profile === "object" &&
              !(profile instanceof Error)
            ) {
              const profileRole =
                (profile as { userRoleCode?: unknown; role?: unknown })
                  .userRoleCode ?? (profile as { role?: unknown }).role;
              if (isAdminRole(profileRole)) {
                nextPath = `/user-admin?${baseQs}`;
              }
            }
          } catch {
            /* stay on dashboard path */
          }

          if (loginBody.readOnly === true) {
            setPendingRedirectPath(nextPath);
            setReadOnlyDialogOpen(true);
          } else {
            setAlertMessage("Login Success");
            setAlertType("success");
            setOpenAlert(true);
            setTimeout(() => {
              setRedirectPath(nextPath);
            }, 1000);
          }
        } else if (response.status === 500) {
          setAlertMessage(
            "Login Error : There is a current user , please login after some time",
          );
          setAlertType("error");
          setOpenAlert(true);
        } else if (response.status === 401) {
          setAlertMessage("Login Error : Invalid credentials or access denied");
          setAlertType("error");
          setOpenAlert(true);
        } else {
          setAlertMessage("Login Error : Unexpected error occurred");
          setAlertType("error");
          setOpenAlert(true);
        }
      } else {
        setAlertMessage("Please fill in all fields");
        setAlertType("error");
        setOpenAlert(true);
      }
    } catch (err: any) {
      setAlertMessage(`Login Error ${err}`);
      setAlertType("error");
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <PreLoader preloaderCheck={loading} />
      ) : (
        <div className="login-page">
          <div className="login-page__left">
            <div className="login-page__decor" aria-hidden="true" />
            <div
              className="login-page__decor login-page__decor--secondary"
              aria-hidden="true"
            />

            <header className="login-page__brand">
              <img
                src="https://agilysys.com/en/wp-content/uploads/Agilysys-Logo-only-RGB.svg"
                alt="Agilysys"
                width={100}
                height={100}
              />
            </header>

            <div className="login-page__form-wrap">
              <form
                className="login-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canSubmit) handleLogin();
                }}
              >
                <h1 className="login-form__title">Sign In</h1>

                <div className="login-field">
                  <label htmlFor="username">Username</label>
                  <input
                    id="username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>

                <div className="login-field login-field--password">
                  <label htmlFor="password">Password</label>
                  <div className="login-field__password-row">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <IconButton
                      className="login-field__toggle"
                      type="button"
                      size="small"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      style={{ padding: "10px" }}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </div>
                </div>

                <div className="login-field">
                  <label htmlFor="tenantId">Tenant ID</label>
                  <input
                    id="tenantId"
                    type="number"
                    min={0}
                    value={tenantID}
                    onChange={(e) => setTenantID(e.target.value)}
                  />
                </div>

                <div className="login-field">
                  <label htmlFor="propertyId">Property ID</label>
                  <input
                    id="propertyId"
                    type="number"
                    min={0}
                    value={propertyID}
                    onChange={(e) => setPropertyID(e.target.value)}
                  />
                </div>

                <div className="login-field login-field--product">
                  <Autocomplete
                    disablePortal
                    options={ProductData}
                    id="productId"
                    value={productID}
                    onChange={(_e, value) => setProductID(value ?? "")}
                    renderOption={(props, option) => (
                      <li {...props} key={option}>
                        <span className="login-product-option">
                          <ProductIcon product={option} />
                          <span>{option}</span>
                        </span>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {productID ? (
                                <InputAdornment position="start">
                                  <ProductIcon product={productID} />
                                </InputAdornment>
                              ) : null}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </div>

                <button
                  type="submit"
                  className="login-btn login-btn--primary"
                  id="loginClick"
                  disabled={!canSubmit}
                >
                  SIGN IN
                </button>

                {/* <div className="login-divider" role="separator">
              <span>Or</span>
            </div> */}

                {/* <button
              type="button"
              className="login-btn login-btn--sso"
              onClick={() => {
                setAlertMessage("SSO sign-in is not configured yet.");
                setAlertType("error");
                setOpenAlert(true);
              }}
            >
              SIGN IN WITH SSO
            </button> */}
              </form>
            </div>

            <footer className="login-page__footer">
              <div className="login-page__footer-brand">
                <img
                  src="https://agilysys.com/en/wp-content/uploads/Agilysys-Logo-only-RGB.svg"
                  alt="agilysys"
                  width={100}
                  height={100}
                />
              </div>
              <div className="login-page__footer-meta">
                <div>© Copyright 2026 Agilysys NV LLC.</div>
                <div>
                  This application is best viewed at 1920x1080 resolution.
                </div>
              </div>
            </footer>
          </div>

          <aside className="login-page__right" aria-hidden="true">
            <img
              className="login-page__right-bg"
              src={rguestPmsBackground}
              alt=""
            />
          </aside>

          {redirectPath && <Navigate to={redirectPath} replace />}
          <AlertComponent
            message={alertMessage}
            type={alertType}
            alertOpen={openAlert}
            closeAlert={() => setOpenAlert(false)}
          />
          <Dialog
            open={readOnlyDialogOpen}
            onClose={() => {}}
            disableEscapeKeyDown
            aria-labelledby="read-only-dialog-title"
          >
            <DialogTitle id="read-only-dialog-title">
              Read-only access
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Your account is in read-only mode. You can review onboarding
                settings, but you cannot change fields, save drafts, submit, or
                delete data.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => {
                  setReadOnlyDialogOpen(false);
                  if (pendingRedirectPath) {
                    setRedirectPath(pendingRedirectPath);
                    setPendingRedirectPath(null);
                  }
                }}
              >
                Continue
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </>
  );
};

export default LoginPage;
